import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { ApexOptions } from 'apexcharts';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import polarisLogo from '/assets/images/other/polaris_logo.jpg';
import ExcelExport from 'export-xlsx';
import autoTable from 'jspdf-autotable';
import { MultiValue } from 'react-select';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export async function enableMocking() {
  const { worker } = await import('../mocks/browser');
  return worker.start();
}

export function isValidDate(dateString: string): boolean {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
}

export const onMouseSidebarEnter = (val: number) => {
  const item = document.getElementById(`sidebar-icon-${val}`);
  if (item) {
    item.setAttribute('fill', 'currentColor');
    const paths = item.querySelectorAll('path');
    paths.forEach((path) => {
      path.style.fill = 'currentColor';
    });
    item.style.color = '#00c4b4';
  }
};

export const onMouseSidebarLeave = (val: number) => {
  const item = document.getElementById(`sidebar-icon-${val}`);
  if (item) {
    item.removeAttribute('fill');
    const paths = item.querySelectorAll('path');
    paths.forEach((path) => {
      path.style.fill = '#89AAEC';
    });
    item.style.color = '';
  }
};

function createUniqueColorGenerator() {
  let counter = 0; // Initialize counter

  function getRandomChannelValue(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  function getUniqueColor() {
    let red, green, blue;

    // Ensure colors are not too dark or too light
    do {
      // Generate random values for the RGB channels
      red = getRandomChannelValue(50, 205); // Avoid too dark (min=50) and too light (max=205)
      green = getRandomChannelValue(50, 205);
      blue = getRandomChannelValue(50, 205);

      // Adjust based on counter to ensure uniqueness
      red = (red + counter * 1234567) & 0xff;
      green = (green + counter * 2345678) & 0xff;
      blue = (blue + counter * 3456789) & 0xff;

      // Increment counter
      counter++;

      // Ensure the color is not too dark or too light
    } while (red + green + blue < 150 || red + green + blue > 600);

    // Convert RGB to hex color
    const color = `#${red.toString(16).padStart(2, '0')}${green
      .toString(16)
      .padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;
    return color;
  }

  return getUniqueColor;
}

export const getUniqueColor = createUniqueColorGenerator();

function getRandomCoordinate(max: number) {
  return Math.floor(Math.random() * max);
}

export function generateRandomShape() {
  return `M ${getRandomCoordinate(50)} ${getRandomCoordinate(
    50
  )} C ${getRandomCoordinate(200)} ${getRandomCoordinate(
    100
  )}, ${getRandomCoordinate(200)} ${getRandomCoordinate(
    100
  )}, ${getRandomCoordinate(200)} ${getRandomCoordinate(
    100
  )} S ${getRandomCoordinate(200)} ${getRandomCoordinate(
    100
  )}, ${getRandomCoordinate(200)} ${getRandomCoordinate(
    100
  )} C ${getRandomCoordinate(200)} ${getRandomCoordinate(
    100
  )}, ${getRandomCoordinate(200)} ${getRandomCoordinate(
    100
  )}, ${getRandomCoordinate(200)} ${getRandomCoordinate(
    100
  )} S ${getRandomCoordinate(200)} ${getRandomCoordinate(
    100
  )}, ${getRandomCoordinate(200)} ${getRandomCoordinate(100)} Z`;
}

export const formatDate = (
  date: string | Date | number | null,
  format: string
): string => {
  if (!date || date === null) return 'Null';
  const options: Intl.DateTimeFormatOptions = {};
  let locale = 'en-US'; // Default locale is 'en-US'

  switch (format) {
    case 'MMM YYYY':
      options.year = 'numeric';
      options.month = 'short';
      break;
    case 'D MMM':
      options.day = 'numeric';
      options.month = 'short';
      break;
    case 'h:mm A':
      options.hour = 'numeric';
      options.minute = 'numeric';
      options.hour12 = true;
      break;
    case 'DD-MM-YYYY':
    case 'DD-MM-YYYY HH:mm:ss':
    case 'DD-MM-YYYY hh:mm:ss':
      options.day = '2-digit';
      options.month = '2-digit';
      options.year = 'numeric';
      locale = 'en-GB'; // Use 'en-GB' for DD-MM-YYYY format
      if (format.includes('HH:mm:ss') || format.includes('hh:mm:ss')) {
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.second = '2-digit';
        options.hour12 = format.includes('hh:mm:ss'); // Use 12-hour format if 'hh:mm:ss'
      }
      break;
    case 'MM-DD-YYYY':
      options.month = '2-digit';
      options.day = '2-digit';
      options.year = 'numeric';
      break;
    case 'HH:mm':
      options.hour = '2-digit';
      options.minute = '2-digit';
      options.hour12 = false;
      break;
    default:
      throw new Error(`Unsupported format: ${format}`);
  }

  return new Intl.DateTimeFormat(locale, options).format(new Date(date));
};

export function formatDateInLocalStr(dateStr: string) {
  if (!dateStr) {
    return;
  }
  const date = new Date(dateStr);

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };

  return date.toLocaleDateString('en-US', options);
}

export function getDateRangesFor7Days(): {
  todayStart: string;
  sevenDaysAgoStart: string;
} {
  const now = new Date();

  // Helper function to format date in "YYYY-MM-DD HH:mm:ss"
  function formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  // Today at 00:00:00
  const todayStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    0,
    0,
    0
  );

  // 7 days before today at 00:00:00
  const sevenDaysAgoStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - 7,
    0,
    0,
    0
  );

  return {
    todayStart: formatDate(todayStart),
    sevenDaysAgoStart: formatDate(sevenDaysAgoStart)
  };
}

export type DataType = {
  title?: string;
  value: number;
  percentage?: number;
  color?: string;
  totalCount?: number;
  data_timestamp: string | Date | number;
};

export type ChartData = {
  [key: string]: DataType[];
};

export const getFormattedCurrentDateTime = () => {
  const addLeadingZero = (num: number) => (num < 10 ? `0${num}` : num);

  const now = new Date();
  const year = now.getFullYear();
  const month = addLeadingZero(now.getMonth() + 1); // Months are 0-based
  const day = addLeadingZero(now.getDate());
  const hours = addLeadingZero(now.getHours()); // 24-hour format by default
  const minutes = addLeadingZero(now.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export function getFormattedPastDate(daysAgo: number) {
  // Ensure currentDate is a Date object
  const date = new Date();

  // Subtract the days
  date.setDate(date.getDate() - daysAgo);

  // Format the result
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}T00:00`;
}

export const prepareChartData = (
  data: ChartData,
  chartType: 'bar' | 'line',
  dateType: 'days' | 'time' | 'month'
) => {
  const location = window && window?.location?.href?.split('/');
  const fileName =
    (location && `${location[location.length - 1]} data`) || 'data';
  const transformDataForChart = (data: DataType[]) => {
    const sortedData = [...data].sort(
      (a, b) =>
        new Date(a.data_timestamp).getTime() -
        new Date(b.data_timestamp).getTime()
    );

    const dateFormat =
      dateType === 'month'
        ? 'MMM YYYY'
        : dateType === 'days'
        ? 'D MMM'
        : 'h:mm A';

    return {
      dates: sortedData.map((item) =>
        formatDate(item.data_timestamp, dateFormat)
      ),
      values: sortedData.map((item) => item.value)
    };
  };

  const collectedDataTransformed = transformDataForChart(data['data1'] || []);
  const missedDataTransformed = transformDataForChart(data['data2'] || []);

  const getLimitedData = (
    data: { dates: string[]; values: number[] },
    limit: number
  ) => {
    const slicedDates = data.dates.slice(0, limit);
    const slicedValues = data.values.slice(0, limit);
    return { dates: slicedDates, values: slicedValues };
  };

  const isMobile = window.innerWidth < 768;
  const limit = isMobile ? 4 : collectedDataTransformed.dates.length; // Limit to 4 on mobile or show all

  const collectedDataLimited = getLimitedData(collectedDataTransformed, limit);
  const missedDataLimited = getLimitedData(missedDataTransformed, limit);

  const series = [
    {
      name: '% Fetched',
      data: collectedDataLimited.values
    },
    {
      name: '% Missed',
      data: missedDataLimited.values
    }
  ];

  const commonOptions: ApexOptions = {
    chart: {
      zoom: { enabled: false },
      type: chartType,
      height: 350,
      toolbar: {
        offsetY: -45,
        tools: {
          download:
            '<img src="/assets/images/other/graphDownload.svg " class="graph-download-icon"> </img>'
        },
        show: true,
        export: {
          csv: {
            filename: fileName
          },
          svg: {
            filename: fileName
          },
          png: {
            filename: fileName
          }
        }
      }
    },
    yaxis: {
      tooltip: {
        enabled: false
      },
      labels: {
        formatter: (val) => `${val}%`,
        style: {
          fontSize: '12px',
          colors: ['#A3B2CF']
        }
      }
    },

    xaxis: {
      tooltip: {
        enabled: false
      },
      categories: collectedDataLimited.dates,
      labels: {
        style: {
          fontSize: '12px',
          colors: collectedDataLimited.dates.map(() => '#A3B2CF')
        },
        format:
          dateType === 'month'
            ? 'MMM yyyy'
            : dateType === 'days'
            ? 'd MMM'
            : 'h:mm TT'
      }
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      itemMargin: {
        horizontal: 20,
        vertical: 5
      }
    },
    tooltip: {
      followCursor: true,
      custom: function ({ series, seriesIndex, dataPointIndex }) {
        return (
          '<div class="arrow_box">' +
          '<span>' +
          `${series[seriesIndex][dataPointIndex]}%` +
          '</span>' +
          '</div>'
        );
      }
    },
    noData: {
      text: 'No data available for this time frame.',
      align: 'center',
      verticalAlign: 'middle',
      offsetX: 0,
      offsetY: 0,
      style: {
        color: '#0A3690',
        fontSize: '20px',
        fontFamily: 'sans-serif'
      }
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 250
          },
          xaxis: {
            categories: collectedDataLimited.dates, // Show only the last 4 entries
            labels: {
              style: {
                fontSize: '8px'
              }
            }
          },
          yaxis: {
            labels: {
              style: {
                fontSize: '8px'
              }
            }
          }
        }
      },
      {
        breakpoint: 480,
        options: {
          chart: {
            height: 200
          },
          xaxis: {
            categories: collectedDataLimited.dates, // Show only the last 4 entries
            labels: {
              style: {
                fontSize: '6px'
              }
            }
          },
          yaxis: {
            labels: {
              style: {
                fontSize: '6px'
              }
            }
          },
          legend: {
            fontSize: '10px',
            markers: {
              size: 4
            }
          },
          noData: {
            text: 'No data available for this time frame.',
            align: 'center',
            verticalAlign: 'middle',
            offsetX: 0,
            offsetY: 0,
            style: {
              color: '#0A3690',
              fontSize: '10px',
              fontFamily: 'sans-serif'
            }
          }
        }
      }
    ]
  };

  const barSpecificOptions: ApexOptions = {
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '50%'
      }
    },
    stroke: {
      colors: ['transparent'],
      width: 5
    },
    colors: ['#0A3690', '#B9CDF4'],
    grid: {
      show: true,
      xaxis: {
        lines: {
          show: false
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      }
    },
    dataLabels: {
      enabled: false
    }
  };

  const lineSpecificOptions: ApexOptions = {
    markers: {
      shape: 'circle',
      size: 5
    },
    colors: ['#0A3690', '#02C9A8'],
    grid: {
      show: true,
      xaxis: {
        lines: {
          show: true
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      }
    },
    stroke: {
      curve: 'smooth',
      width: 2
    }
  };

  const options = {
    ...commonOptions,
    ...(chartType === 'bar' ? barSpecificOptions : lineSpecificOptions)
  };

  return { series, options };
};

export function trimDecimals(value: number, decimals: number): number {
  if (!Number.isFinite(value) || decimals < 0) return value;

  // If value is an integer, return it as-is
  if (Number.isInteger(value)) return value;

  const [intPart, decimalPart = ''] = value.toString().split('.');

  if (decimalPart.length === 0) return value; // no decimal part

  const trimmed = decimalPart.substring(0, decimals);
  return parseFloat(`${intPart}.${trimmed}`);
}


export function dateDiffInDays(dateTime1: string, dateTime2: string): number {
  const date1 = new Date(dateTime1);
  const date2 = new Date(dateTime2);
  const diffInMilliseconds = Math.abs(date2.getTime() - date1.getTime());
  const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);
  return diffInDays;
}

export function dateDiffInMonths(dateTime1: string, dateTime2: string): number {
  const date1 = new Date(dateTime1);
  const date2 = new Date(dateTime2);
  const yearsDiff = date2.getFullYear() - date1.getFullYear();
  const monthsDiff = date2.getMonth() - date1.getMonth();
  return yearsDiff * 12 + monthsDiff;
}

export function formatString(input: string): string {
  if (input.includes('_')) {
    // Handle snake_case: Replace underscores with spaces and capitalize each word
    return input
      .split('_') // Split by underscore
      .map((word) => word.toUpperCase()) // Convert each word to uppercase
      .join(' '); // Join with spaces
  } else {
    // Handle camelCase: Insert space before each capital letter and capitalize each word
    return input
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space before each capital letter
      .toUpperCase(); // Convert everything to uppercase
  }
}

export function downloadTableAsPDF<T extends object>(
  tableName: string,
  tblData: T[],
  ignoreColumns: string[] = ['args', 'colorCode'] // Parameter to specify ignored columns
) {
  if (tblData.length === 0) return;

  const unit = 'pt';
  const size = 'A4'; // Use A1, A2, A3, or A4
  const orientation =
    Object.keys(tblData[0]).length < 6 ? 'portrait' : 'landscape'; // portrait or landscape

  const marginLeft = 40;
  const marginTop = 50;
  const headerOffset = 110; // Increased offset to prevent overlap with the logo on new pages
  const doc = new jsPDF({ orientation, unit, format: size });

  // Set initial font size
  const titleFontSize = 18;
  const contentFontSize = 12;

  doc.setFontSize(titleFontSize);
  const title = tableName;

  // Add the header
  const addHeader = () => {
    const logoWidth = 70; // Adjust the logo width as needed
    const logoHeight = 70; // Adjust the logo height as needed
    const marginLeft = 20; // Ensure consistent left margin
    const titleFontSize = 14; // Slightly smaller font size for the title
    const titleY = logoHeight + 10; // Position title below the logo with some spacing

    // Calculate X and Y coordinates for the logo
    const logoX = doc.internal.pageSize.width - logoWidth - marginLeft; // Align to the right
    const logoY = 5; // Move the logo down a bit from the top

    // Add logo image to the header
    doc.addImage(polarisLogo, 'JPG', logoX, logoY, logoWidth, logoHeight);

    // Add the title below the logo
    doc.setFontSize(titleFontSize); // Set a smaller font size for the title
    doc.text(title || '', marginLeft, logoY + titleY); // Position the title below the logo
  };

  // Define the footer content
  const footerHeight = 30; // Adjust the footer height as needed
  const footerText =
    'Polaris Smart Metering Pvt. Ltd., E-418, Road No. 14, V.K.I. Area, Jaipur, Rajasthan-302013, India ';

  // Add the footer to each page
  const addFooter = (pageCount: number) => {
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageHeight = doc.internal.pageSize.height;
      const footerY = pageHeight - footerHeight;

      doc.setFontSize(10); // Ensure consistent footer font size
      doc.text(footerText, marginLeft, footerY + 15);
      doc.line(
        marginLeft,
        footerY,
        doc.internal.pageSize.width - marginLeft,
        footerY
      );
    }
  };

  // Prepare data for PDF in transposed format
  // eslint-disable-next-line
  const transposedData: any[] = [];
  const headers = Object.keys(tblData[0]).filter(
    (key) => !ignoreColumns.includes(key)
  );

  headers.forEach((header) => {
    const row = [`${formatString(header)}\u0001`]; // Use \u0001 as a marker to make this cell bold
    const headerNew = header as keyof T;

    tblData.forEach((dataRow) => {
      const rowVal =
        dataRow[headerNew] === '65535-00-00 00:00:00' ||
        dataRow[headerNew] === '1970-01-01 00:00:00'
          ? '-'
          : dataRow[headerNew] === 0
          ? '0'
          : (dataRow[headerNew] as string) || '--';
      row.push(rowVal);
    });
    transposedData.push(row);
  });

  // Add the first page header
  addHeader();

  // Add table in transposed format
  doc.setFontSize(contentFontSize);
  let currentY = marginTop + 60; // Adjusted to account for the header
  autoTable(doc, {
    startY: currentY,
    head: [['Field', ...tblData.map((_, index) => `Row ${index + 1}`)]],
    body: transposedData,
    margin: { left: marginLeft, right: marginLeft },
    theme: 'grid',
    styles: {
      fontSize: contentFontSize - 2,
      cellWidth: 'auto',
      overflow: 'linebreak'
    },
    headStyles: { fillColor: [10, 54, 144] },
    columnStyles: {
      0: { cellWidth: 80 }
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 0) {
        data.cell.styles.fontStyle = 'bold';
      }
    },
    // eslint-disable-next-line
    didDrawPage: (data: any) => {
      currentY = data.cursor.y;
      doc.setFontSize(contentFontSize);
      currentY += headerOffset;
    },
    pageBreak: 'auto'
  });

  const pageCount = doc.internal.pages.length; // Get the page count after adding the table
  addFooter(pageCount); // Pass the page count to addFooter
  doc.save(`${tableName}.pdf`);
}

// Function to download data in Excel format
export const downloadExcel = (
  tableName: string,
  // eslint-disable-next-line
  tableData: any
): void => {
  // Utility function to sanitize the sheet name
  const sanitizeSheetName = (name: string): string => {
    return name.replace(/[*?:\\/[\]]/g, '_'); // Replace invalid characters with an underscore
  };

  // Format table data for download
  const modifiedData = tableData.map(
    (item: { [key: string]: string | number | undefined }) => {
      const copy: { [key: string]: string | number | undefined } = { ...item };
      for (const key in copy) {
        if (
          copy[key] === null ||
          copy[key] === '' ||
          copy[key] === '65535-00-00 00:00:00' ||
          copy[key] === '1970-01-01 00:00:00'
        ) {
          copy[key] = '--';
        }
        const modifiedKey = key
          .replace(/_/g, ' ')
          .replace(/^\w/, (c) => c.toUpperCase());

        if (modifiedKey !== key) {
          copy[modifiedKey] = copy[key];
          delete copy[key];
        }
      }
      return copy;
    }
  );

  const dataTemp = [{ table1: modifiedData }];

  // Extract columns from the data itself
  const columnName = Object.keys(modifiedData[0] || {}).map((key) => ({
    name: key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
    key: key,
    width: key === 'Event message' ? 50 : 25
  }));

  const sanitizedSheetName = sanitizeSheetName(tableName);

  const dataTempFormat = {
    // Table settings
    fileName: tableName,
    workSheets: [
      {
        sheetName: sanitizedSheetName, // Use sanitized sheet name
        startingRowNumber: 2,
        gapBetweenTwoTables: 2,
        tableSettings: {
          table1: {
            tableTitle: `${tableName} (Polaris Smart Metering)`,
            headerDefinition: columnName
          }
        }
      }
    ]
  };

  const excelExport = new ExcelExport();
  excelExport.downloadExcel(dataTempFormat, dataTemp);
};

export function downloadCSV<T extends object>(data: T[], title?: string) {
  if (data.length === 0) return;

  // Utility function to escape commas and quotes
  const escapeCell = (
    cell: number | boolean | null | undefined | string
  ): string => {
    if (
      cell === null ||
      cell === undefined ||
      cell === '65535-00-00 00:00:00' ||
      cell === '1970-01-01 00:00:00'
    ) {
      return '--';
    }
    const cellValue =
      typeof cell === 'object' ? JSON.stringify(cell) : cell.toString();
    // Wrap in quotes if it contains a comma, double quotes, or newlines
    if (/[",\n]/.test(cellValue)) {
      return `"${cellValue.replace(/"/g, '""')}"`; // Escape double quotes by doubling them
    }
    return cellValue;
  };

  // Extract the headers
  const headers = Object.keys(data[0]).map(escapeCell).join(',');

  // Extract rows from table data
  const rows = data.map((record) => {
    return Object.values(record).map(escapeCell).join(',');
  });

  // Combine title, headers, and rows into a single CSV string
  const csvContent = `${title || 'Polaris Smart Metering'}\n\n${[
    headers,
    ...rows
  ].join('\n')}`;

  // Create a Blob from the CSV content
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // Create a link and trigger the download
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'table_data.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

type ModuleArrayType = MultiValue<{
  label: string;
  module: string;
  route: string;
  value: string;
}>;

export const modulesArray: ModuleArrayType = [
  {
    label: 'HES',
    value: '/utilility/uppcl/hes',
    route: '/utilility/uppcl/hes',
    module: 'hes'
  },
  {
    label: 'VEE',
    value: '/utilility/uppcl/vee',
    route: '/utilility/uppcl/vee',
    module: 'vee'
  },
  {
    label: 'NFMS',
    value: '/utilility/uppcl/nfms',
    route: '/utilility/uppcl/nfms',
    module: 'nfms'
  },
  {
    label: 'SAT',
    value: '/utilility/uppcl/sat',
    route: '/utilility/uppcl/sat',
    module: 'sat'
  }
];
